import { callClaude } from '../lib/claude';
import React, { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import ScheduleGenerator from './ScheduleGenerator';
import ImportWizard from './ImportWizard';

const BUCKET = 'item-documents';
const MAX_SIZE = 20 * 1024 * 1024;

const DOC_TYPE_STATUS = {
  'Quote': 'Quote Received',
  'Proposal': 'Proposal Received', 
  'PO': 'PO Received',
};

const CONFIDENCE_COLORS = {
  high:   { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
  medium: { bg: '#fff8e1', text: '#e65100', border: '#ffe082' },
  low:    { bg: '#fff5f5', text: '#c0392b', border: '#fcc' },
};

// Sub-tab button
function SubTab({ label, active, onClick, badge }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 16px', border: 'none',
      borderBottom: active ? '2px solid #1a1a1a' : '2px solid transparent',
      background: 'none', fontWeight: active ? 700 : 400, fontSize: 13,
      color: active ? '#1a1a1a' : '#888', cursor: 'pointer', whiteSpace: 'nowrap',
      position: 'relative',
    }}>
      {label}
      {badge ? (
        <span style={{
          marginLeft: 6, background: '#e65100', color: '#fff',
          fontSize: 9, fontWeight: 700, padding: '1px 5px',
          borderRadius: 8, verticalAlign: 'middle',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}


// ── SMART DOCUMENT SCANNER ───────────────────────────────────────────────────
function SmartScanner({ items, projects, docTypeName, onUpdateItem, onDocAttached }) {
  const [text, setText] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [matches, setMatches] = useState(null);
  const [confirmed, setConfirmed] = useState({});
  const [overrides, setOverrides] = useState({});
  const [uploading, setUploading] = useState({});
  const [done, setDone] = useState({});
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const allParents = (items || []).filter(it => !it.parentId);
  const parents = projectFilter
    ? allParents.filter(it => it.projectLabel === projectFilter || it.project === projectFilter)
    : allParents;

  function callClaude(sys, usr, maxTokens) {
    maxTokens = maxTokens || 1600;
    return fetch('/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system: sys, user: usr, max_tokens: maxTokens }),
    }).then(r => r.json()).then(d => d.text || '');
  }

  async function handleFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > MAX_SIZE) { setError('File too large — maximum 20MB.'); return; }
    setFile(f);
    setError('');
    // Read text content for scanning
    const reader = new FileReader();
    reader.onload = ev => setText(ev.target.result);
    reader.onerror = () => setText('');
    reader.readAsText(f);
  }

  async function scan() {
    if (!text.trim()) { setError('Please paste document text or upload a text/PDF file.'); return; }
    setScanning(true); setMatches(null); setConfirmed({}); setOverrides({}); setDone({}); setError('');

    // Build item summary for Claude
    const itemSummary = parents.map(it => ({
      id: it.id,
      item: it.item,
      vendor: it.vendor || '',
      project: it.projectLabel || it.project || '',
      room: it.roomName || '',
      manufacturer: it.specs?.manufacturer || '',
      model: it.specs?.model || '',
      dimensions: it.specs?.dimensions || '',
      sidemark: it.sidemark || '',
      status: it.status,
    }));

    const sys = `You are a document matching assistant for Cimon Starr Interior Design.
Match line items in a ${docTypeName} document against a list of database items.
Return JSON only, no markdown.
Format: {
  "docSummary": "brief description of the document",
  "vendor": "vendor name if found",
  "docRef": "quote/proposal/PO number if found",
  "matches": [
    {
      "itemId": "exact id from database",
      "itemName": "matched item name from database",
      "confidence": "high|medium|low",
      "reason": "why this matches — e.g. same vendor + model number",
      "docLineItem": "the line item text from the document",
      "docPrice": "price found in document if any",
      "docLeadTime": "lead time if found",
      "docQty": "quantity if found"
    }
  ],
  "unmatched": ["line items from document that could not be matched"]
}
Confidence: high = 2+ matching fields (vendor+model, name+dimensions, sidemark etc), medium = 1 field, low = partial/uncertain.
Only include items you are reasonably confident about. It is better to return fewer high-confidence matches than many low-confidence ones.`;

    const usr = `Database items:
${JSON.stringify(itemSummary, null, 2)}

${docTypeName} document:
${text}`;

    try {
      const res = await callClaude(sys, usr);
      const parsed = JSON.parse(res.replace(/\`\`\`json|\`\`\`/g, '').trim());
      setMatches(parsed);
    } catch (e) {
      setError('Could not parse document — try pasting more complete text.');
    }
    setScanning(false);
  }

  async function confirmMatch(match, itemOverrideId) {
    const targetId = itemOverrideId || match.itemId;
    const item = parents.find(it => String(it.id) === String(targetId));
    if (!item) { setError('Item not found.'); return; }

    setUploading(prev => ({ ...prev, [match.itemId]: true }));

    // Determine new status
    const newStatus = DOC_TYPE_STATUS[docTypeName] || item.status;

    // Upload file to Supabase Storage if we have one
    if (file) {
      const path = `${targetId}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type || 'application/octet-stream' });
      if (!upErr) {
        const label = `${docTypeName}${matches.docRef ? ' ' + matches.docRef : ''} — ${matches.vendor || 'Vendor'}`;
        await supabase.from('item_documents').insert({
          item_id: String(targetId),
          label,
          doc_type: docTypeName,
          file_name: file.name,
          file_path: path,
          file_size: file.size,
          mime_type: file.type || '',
        });
      }
    }

    // Update item status + pricing
    const patch = { status: newStatus };
    if (match.docPrice) patch.unitCost = match.docPrice;
    if (match.docLeadTime) patch.leadTime = match.docLeadTime;
    if (matches.docRef) patch.proposalRef = matches.docRef;
    onUpdateItem(targetId, { ...item, ...patch });

    setUploading(prev => ({ ...prev, [match.itemId]: false }));
    setDone(prev => ({ ...prev, [match.itemId]: true }));
    if (onDocAttached) onDocAttached();
  }

  const inp = {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
    borderRadius: 6, fontSize: 12, fontFamily: 'inherit',
    outline: 'none', background: '#fff', boxSizing: 'border-box',
  };

  const docColor = docTypeName === 'Quote' ? '#0277bd' : docTypeName === 'Proposal' ? '#c2185b' : '#4527a0';
  const docBg = docTypeName === 'Quote' ? '#e8f0fe' : docTypeName === 'Proposal' ? '#fce4ec' : '#ede7f6';
  const docBorder = docTypeName === 'Quote' ? '#90caf9' : docTypeName === 'Proposal' ? '#f48fb1' : '#ce93d8';

  return (
    <div>
      <div style={{ background: docBg, border: `1px solid ${docBorder}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: docColor }}>
        <strong>{docTypeName} Received</strong> — Paste or upload the document. Claude will find the matching items in your database and suggest attaching it.
      </div>

      {error && (
        <div style={{ background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#c0392b', marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Left: input */}
        <div>
          {/* Project filter */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>FILTER TO PROJECT (recommended)</label>
            <select value={projectFilter} onChange={e => { setProjectFilter(e.target.value); setMatches(null); }}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, background: '#fff' }}>
              <option value="">All Projects</option>
              {(projects || []).map(p => <option key={p.id} value={p.label}>{p.label}</option>)}
            </select>
            {!projectFilter && <div style={{ fontSize: 10, color: '#e65100', marginTop: 3 }}>⚠ No project selected — matches may include items from other projects</div>}
          </div>

          {/* File upload */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input ref={fileRef} type="file" onChange={handleFile} style={{ display: 'none' }} />
            <button onClick={() => fileRef.current.click()} style={{
              padding: '6px 14px', background: '#fff', border: '1px solid #ddd',
              borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, color: '#444',
            }}>Upload File</button>
            {file && <span style={{ fontSize: 11, color: '#2e7d32', fontWeight: 600 }}>✓ {file.name}</span>}
            <span style={{ fontSize: 11, color: '#aaa' }}>or paste text below</span>
          </div>

          <textarea value={text} onChange={e => { setText(e.target.value); setMatches(null); }}
            placeholder={`Paste ${docTypeName.toLowerCase()} text here...`}
            rows={12} style={{ ...inp, resize: 'vertical', marginBottom: 10 }} />

          <button onClick={scan} disabled={!text.trim() || scanning} style={{
            padding: '9px 20px', background: docColor, color: '#fff',
            border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: text.trim() && !scanning ? 'pointer' : 'not-allowed',
            opacity: text.trim() && !scanning ? 1 : 0.5,
          }}>
            {scanning ? 'Scanning...' : `Scan & Match ${docTypeName}`}
          </button>
        </div>

        {/* Right: matches */}
        <div>
          {!matches && (
            <div style={{ background: '#fafafa', border: '1px dashed #ddd', borderRadius: 8, padding: 16, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 13 }}>
              Matches will appear here
            </div>
          )}

          {matches && (
            <div>
              {/* Doc summary */}
              <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#333' }}>{matches.docSummary}</div>
                {matches.vendor && <div style={{ color: '#666', marginTop: 2 }}>Vendor: {matches.vendor}</div>}
                {matches.docRef && <div style={{ color: '#666' }}>Ref: {matches.docRef}</div>}
              </div>

              {/* Matches */}
              {(matches.matches || []).length === 0 && (
                <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 12 }}>
                  No matches found in your database. Check that items are entered with vendor and description details.
                </div>
              )}

              {(matches.matches || []).map((match) => {
                const cc = CONFIDENCE_COLORS[match.confidence] || CONFIDENCE_COLORS.low;
                const isDone = done[match.itemId];
                const isUploading = uploading[match.itemId];
                const overrideId = overrides[match.itemId];

                return (
                  <div key={match.itemId} style={{
                    border: `1px solid ${isDone ? '#a5d6a7' : cc.border}`,
                    borderRadius: 8, padding: 12, marginBottom: 10,
                    background: isDone ? '#f0fff4' : '#fff',
                  }}>
                    {/* Confidence badge */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a1a' }}>
                        {isDone ? '✓ ' : ''}{match.itemName}
                      </div>
                      <span style={{ background: cc.bg, color: cc.text, fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 8, whiteSpace: 'nowrap', marginLeft: 8 }}>
                        {match.confidence.toUpperCase()} MATCH
                      </span>
                    </div>

                    {/* Doc line item */}
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontStyle: 'italic' }}>
                      "{match.docLineItem}"
                    </div>

                    {/* Reason */}
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>
                      {match.reason}
                    </div>

                    {/* Extracted data */}
                    <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#666', marginBottom: 8, flexWrap: 'wrap' }}>
                      {match.docPrice && <span>Price: <strong>{match.docPrice}</strong></span>}
                      {match.docLeadTime && <span>Lead: <strong>{match.docLeadTime}</strong></span>}
                      {match.docQty && <span>Qty: <strong>{match.docQty}</strong></span>}
                    </div>

                    {!isDone && (
                      <>
                        {/* Override select */}
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: '#aaa', marginBottom: 3 }}>WRONG ITEM? SELECT CORRECT ONE:</div>
                          <select value={overrideId || match.itemId} onChange={e => setOverrides(prev => ({ ...prev, [match.itemId]: e.target.value }))}
                            style={{ ...inp, fontSize: 11 }}>
                            {parents.map(it => (
                              <option key={it.id} value={it.id}>
                                {it.item} — {it.projectLabel || it.project || ''} / {it.roomName || ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => confirmMatch(match, overrideId)} disabled={isUploading}
                            style={{ padding: '6px 14px', background: docColor, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: isUploading ? 0.6 : 1 }}>
                            {isUploading ? 'Attaching...' : `Confirm & Attach`}
                          </button>
                          <button onClick={() => setDone(prev => ({ ...prev, [match.itemId]: true }))}
                            style={{ padding: '6px 12px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#888' }}>
                            Skip
                          </button>
                        </div>
                      </>
                    )}

                    {isDone && (
                      <div style={{ fontSize: 11, color: '#2e7d32', fontWeight: 600 }}>
                        ✓ Attached · Status advanced to {DOC_TYPE_STATUS[docTypeName]}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Unmatched */}
              {(matches.unmatched || []).length > 0 && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#e65100', marginBottom: 4 }}>NOT MATCHED:</div>
                  {matches.unmatched.map((u, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#666' }}>• {u}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function DocumentsTab({ items, projects, onUpdateItem, onAddItems, onImportVendors, vendors, onAddVendor }) {
  const [sub, setSub] = useState('import');

  return (
    <div>
      {/* Sub-nav */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #e0e0e0', marginBottom: 24, overflowX: 'auto' }}>
        <SubTab label="Import Tool" active={sub === 'import'} onClick={() => setSub('import')} />
        <SubTab label="Quote Received" active={sub === 'quote'} onClick={() => setSub('quote')} />
        <SubTab label="Proposal Received" active={sub === 'proposal'} onClick={() => setSub('proposal')} />
        <SubTab label="PO Received" active={sub === 'po'} onClick={() => setSub('po')} />
        <SubTab label="Generate Schedules" active={sub === 'schedules'} onClick={() => setSub('schedules')} />
      </div>

      {/* ── IMPORT ── */}
      {sub === 'import' && (
        <ImportWizard projects={projects} existingItems={items} onImport={(importedItems) => {
          if (onAddItems) onAddItems(importedItems);
        }} onImportVendors={onImportVendors} vendors={vendors} onAddVendor={onAddVendor} />
      )}

      {/* ── QUOTE RECEIVED ── */}
      {sub === 'quote' && (
        <SmartScanner items={items} projects={projects} docTypeName="Quote" onUpdateItem={onUpdateItem} />
      )}

            {/* ── PROPOSAL RECEIVED ── */}
      {sub === 'proposal' && (
        <SmartScanner items={items} projects={projects} docTypeName="Proposal" onUpdateItem={onUpdateItem} />
      )}

            {/* ── PO RECEIVED ── */}
      {sub === 'po' && (
        <SmartScanner items={items} projects={projects} docTypeName="PO" onUpdateItem={onUpdateItem} />
      )}

            {/* ── GENERATE SCHEDULES ── */}
      {sub === 'schedules' && (
        <ScheduleGenerator items={items} projects={projects} />
      )}
    </div>
  );
}
